import Model from '../common/models/Model';
import React, { useContext, useMemo, useState } from 'react';
import { BrandMemberRole } from '../../types/enums';
import { Button } from '../common/Button';
import Input from '../common/Input';
import useI18n from '../../hooks/useI18n';
import { toast } from 'react-toastify';
import { useUpdateBrandUserRole } from '@/hooks/queries/useUserQueries';
import { UserContext } from '@/context/UserContext';

interface EditUserRoleProps {
  isEditRoleModalOpen: boolean;
  setIsEditRoleModalOpen: (arg: boolean) => void;
  userId: string;
  userName: string;
  userEmail: string;
  userRole: string;
  onSuccess: () => void;
  setIsLoading: (arg: boolean) => void;
}

const EditUserRole: React.FC<EditUserRoleProps> = ({
  isEditRoleModalOpen,
  setIsEditRoleModalOpen,
  userId,
  userName,
  userEmail,
  userRole,
  onSuccess,
  setIsLoading,
}) => {
  const { t } = useI18n();
  const [newRole, setNewRole] = useState('');
  const updateRoleMutation = useUpdateBrandUserRole();
  const userContext = useContext(UserContext);
  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }
  const { user } = userContext;
  const isManager = useMemo(
    () => user?.brandMemberRole === BrandMemberRole.Manager,
    [user?.brandMemberRole],
  );
  const isSelf = useMemo(() => user?._id === userId, [user?._id, userId]);

  const handleUpdateRole = async () => {
    if (!newRole) {
      toast.error(t('errors.roleRequired'));
      return;
    }
    setIsLoading(true);
    setIsEditRoleModalOpen(false);

    try {
      await updateRoleMutation.mutateAsync({ userId, role: newRole });
      toast.success(t('userManagement.userUpdatedSuccess'));
      onSuccess();
    } catch (error) {
      toast.error(t('errors.failedToUpdateRole'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Model
      isOpen={isEditRoleModalOpen}
      onClose={() => setIsEditRoleModalOpen(false)}
      className="backdrop-brightness-75"
    >
      <div className="md:w-[400px]">
        <h1 className="text-2xl font-header text-[#212121]">
          {t('userManagement.actions.editRole')}
        </h1>
        <Input
          type="text"
          placeholder={String(t('userManagement.fullName'))}
          className="my-[16px] font-content"
          value={userName}
          disabled
        />
        <Input
          type="text"
          placeholder={String(t('userManagement.workEmail'))}
          className="mb-[16px] font-content"
          value={userEmail}
          disabled
        />
        <select
          className="w-full cursor-pointer p-3 rounded-full border outline-none border-gray-400 mb-[16px]"
          value={newRole}
          onChange={(e) => setNewRole(e.target.value as BrandMemberRole)}
        >
          <option value="" disabled>
            {t('userManagement.selectRole')}
          </option>
          <option
            value={BrandMemberRole.User}
            disabled={userRole === BrandMemberRole.User} // Disable if user already has this role
          >
            {t('userManagement.roles.user')}
          </option>
          <option
            value={BrandMemberRole.Manager}
            disabled={
              userRole === BrandMemberRole.Manager || // Disable if user already has this role
              (isManager && isSelf) ||
              (isManager && userRole !== BrandMemberRole.User)
            }
          >
            {t('userManagement.roles.manager')}
          </option>
          <option
            value={BrandMemberRole.Admin}
            disabled={
              userRole === BrandMemberRole.Admin || // Disable if user already has this role
              isSelf ||
              isManager
            }
          >
            {t('userManagement.roles.admin')}
          </option>
        </select>
        <div className="mt-2 flex justify-end items-center gap-2">
          <Button
            onClick={() => setIsEditRoleModalOpen(false)}
            variant="outlined"
          >
            {t('common.cancel')}
          </Button>
          <Button onClick={handleUpdateRole}>
            {t('userManagement.actions.update')}
          </Button>
        </div>
      </div>
    </Model>
  );
};

export default EditUserRole;
